import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { HeaderNav } from "../components/HeaderNav";
import { Footer } from "../components/Footer";
import { Marquee } from "../components/Marquee";
import { RESUME_URL } from "../lib/data";
import {
  ArrowUpRight, Download, Home, Quote, BatteryCharging, Gauge, Bell,
  Sparkles, MapPin, Zap, Navigation, ShieldCheck, Wrench,
} from "lucide-react";

/* ---------- Motion helpers ---------- */
const reveal = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const MaskLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

/* Small original bolt mark (not the client's logo) */
const Bolt = ({ light = false, size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="15" stroke={light ? "#FFFFFF" : "#312E81"} strokeWidth="1.6" />
    <path d="M17.5 6L10 17.5h5L14.5 26L22 14.5h-5L17.5 6z" fill={light ? "#FFFFFF" : "#312E81"} />
  </svg>
);

/* ---------- Data ---------- */
const PERSONAS = [
  {
    name: "Arjun Mehta",
    photo: "/projects/ather/persona-arjun.jpg",
    meta: "29 · Bengaluru · Product Analyst",
    quote: "I don't want to think about my vehicle every day. It should just work — quietly, predictably, and without surprises.",
    goals: [
      "Get to work and back without thinking about range",
      "Charge easily and predictably; know vehicle health at a glance",
      "Be seen as practical and future-forward — not an early-adopter guinea pig",
    ],
    pains: [
      "Fear of real-world range vs. claimed range",
      "Anxiety on unfamiliar routes; inconsistent public charging",
      "Worry that software issues could mean vehicle failure",
    ],
    expects: [
      "Range estimates that feel honest & conservative",
      "Clear alerts, not alarming ones — minimal cognitive load",
      "High trust in Ather Grid availability with clear fallbacks",
    ],
  },
  {
    name: "Reshma & Rajat Kapoor",
    photo: "/projects/ather/persona-couple.jpg",
    meta: "41 · Pune · Working parents, 2 kids",
    quote: "We prioritize practicality and safety. Our scooter should reliably fit into our busy family life without becoming an extra headache.",
    goals: [
      "Commute and complete tasks reliably",
      "Keep the family safe during every trip",
      "Minimize time and cost spent on maintenance",
    ],
    pains: [
      "Uncertain about vehicle longevity & battery lifespan",
      "Stress about battery draining faster under heavy loads",
      "Frustration with slow, unreliable public chargers",
    ],
    expects: [
      "Consistency & predictability over sportiness",
      "Family-friendly, reliable charging options",
      "Transparent servicing — no pricing surprises",
    ],
  },
];

const CHAPTERS = [
  { n: "01", label: "Business Insights", body: "Aligned on product purpose, positioning and value proposition — and the critical success factors for winning trust in a skeptical, value-first market.", out: "Design Brief" },
  { n: "02", label: "User Research", body: "Firmed up mental-model personas, journeys, tasks and scenarios through qualitative and quantitative studies — and evaluated the current experience.", out: "Insights" },
  { n: "03", label: "UX Measurement", body: "Defined measurable UX parameters — effectiveness, efficiency, learnability, memorability — backed by web data, surveys and NPS.", out: "Metrics" },
  { n: "04", label: "Concept Design", body: "Established the foundation: information grouping, navigation and conceptual models, plus communication and visual-experience strategy.", out: "IA & Strategy" },
  { n: "05", label: "Formative Testing", body: "Validated the concept with users on expectancy, first impression, discoverability, comprehension and flow pain points.", out: "UT Report" },
  { n: "06", label: "Detailed Design", body: "Produced detailed wireframes and high-fidelity mocks for every critical task and page type, including micro-interactions.", out: "Wireframes & UI" },
  { n: "07", label: "Front-end Direction", body: "Set device-agnostic, responsive standards — HTML5/CSS3 tested across critical browsers with clear component guidelines.", out: "Design & Dev Standards" },
];

const TENETS = [
  { icon: Gauge, title: "Honest range, not optimistic range", body: "Conservative, trustworthy estimates that never over-promise — because trust is the real range." },
  { icon: Bell, title: "Calm alerts, not alarms", body: "Clear, non-distracting information that lowers cognitive load instead of adding to it." },
  { icon: Sparkles, title: "A predictive companion", body: "The app guides proactively — planning charges and trips — rather than reacting after the fact." },
  { icon: MapPin, title: "Charging you can see", body: "Visibility into Ather Grid availability and reliability, with clear fallbacks when a charger fails." },
];

/* ---------- Reusable ---------- */
const Chapter = ({ n, label, title, children }) => (
  <section className="max-w-5xl mx-auto px-6 py-16 border-t border-black/5">
    <motion.div {...reveal} className="grid md:grid-cols-12 gap-8">
      <div className="md:col-span-4">
        <div className="flex items-baseline gap-4">
          <span className="font-serif text-5xl text-primary/20 leading-none">{n}</span>
          <span className="overline text-primary">{label}</span>
        </div>
        {title && <h2 className="font-serif text-3xl md:text-4xl text-foreground mt-5 leading-tight">{title}</h2>}
      </div>
      <div className="md:col-span-7 md:col-start-6 text-lg text-foreground/70 leading-relaxed space-y-4">
        {children}
      </div>
    </motion.div>
  </section>
);

/* ---------- Native app screens (Interface direction) ---------- */
const PhoneFrame = ({ children, label }) => (
  <div className="shrink-0">
    <div className="relative w-[248px] rounded-[2.2rem] bg-[#0A0A0C] border border-white/10 p-3 shadow-[0_30px_70px_rgba(0,0,0,0.35)]">
      <div className="rounded-[1.7rem] overflow-hidden bg-[#0A0A0C] h-[512px] p-5">{children}</div>
    </div>
    {label && <div className="mt-4 text-center text-[10px] font-mono uppercase tracking-widest text-foreground/40">{label}</div>}
  </div>
);

const StatusBar = () => (
  <div className="flex items-center justify-between text-white/50 text-[10px] font-mono uppercase tracking-widest mb-6">
    <span className="inline-flex items-center gap-1.5"><Bolt light size={16} /> Ather</span>
    <span>09:41</span>
  </div>
);

const ScreenRide = () => (
  <>
    <StatusBar />
    <div className="flex items-end gap-2">
      <span className="font-serif text-white text-6xl leading-none">128</span>
      <span className="text-white/50 mb-2 text-sm">km range</span>
    </div>
    <div className="mt-1 text-white/40 text-[11px]">Estimated conservatively · live</div>
    <div className="mt-5 h-2 rounded-full bg-white/10 overflow-hidden">
      <div className="h-full w-[72%] rounded-full bg-gradient-to-r from-[#6366F1] to-[#A5B4FC]" />
    </div>
    <div className="mt-1.5 flex justify-between text-white/40 text-[10px] font-mono"><span>72%</span><span>Full by 7:30 AM</span></div>
    <div className="mt-5 grid grid-cols-3 gap-2.5">
      {[["24", "km/h"], ["18", "trips"], ["A+", "health"]].map(([v, l]) => (
        <div key={l} className="rounded-xl bg-white/5 border border-white/10 px-2.5 py-2.5">
          <div className="text-white font-serif text-lg">{v}</div>
          <div className="text-white/40 text-[9px] font-mono uppercase tracking-widest">{l}</div>
        </div>
      ))}
    </div>
    <div className="mt-4 flex items-center gap-2 rounded-xl bg-[#312E81]/40 border border-[#6366F1]/30 px-3 py-2.5 text-[#C7D2FE] text-[11px]">
      <BatteryCharging size={13} /> Charge tonight to reach Pune comfortably.
    </div>
  </>
);

const ScreenGrid = () => (
  <>
    <StatusBar />
    <div className="text-white font-serif text-2xl leading-tight">Ather Grid</div>
    <div className="text-white/40 text-[11px] mt-1">4 chargers near you</div>
    <div className="mt-4 rounded-2xl overflow-hidden border border-white/10 h-28 relative bg-[#141417]">
      <div className="absolute inset-0" style={{ background: "radial-gradient(60% 80% at 40% 30%, rgba(99,102,241,0.35), transparent 70%)" }} />
      <MapPin size={22} className="text-[#A5B4FC] absolute left-10 top-8" />
      <MapPin size={16} className="text-white/40 absolute right-12 bottom-6" />
      <Navigation size={14} className="text-white/50 absolute right-6 top-6" />
    </div>
    <div className="mt-4 space-y-2.5">
      {[["Indiranagar", "Available · 2", "#4ADE80"], ["Koramangala", "Busy · 0", "#F59E0B"], ["HSR Layout", "Available · 3", "#4ADE80"]].map(([n, s, c]) => (
        <div key={n} className="flex items-center justify-between rounded-xl bg-white/5 border border-white/10 px-3 py-2.5">
          <div>
            <div className="text-white text-xs">{n}</div>
            <div className="text-white/40 text-[10px] font-mono">{s}</div>
          </div>
          <span className="h-2 w-2 rounded-full" style={{ background: c }} />
        </div>
      ))}
    </div>
  </>
);

const ScreenTrip = () => (
  <>
    <StatusBar />
    <div className="text-white font-serif text-2xl leading-tight">Plan a trip</div>
    <div className="text-white/40 text-[11px] mt-1">Bengaluru → Mysuru</div>
    <div className="mt-4 rounded-2xl bg-white/5 border border-white/10 p-4">
      <div className="flex items-center gap-3 text-white text-xs"><span className="h-2 w-2 rounded-full bg-[#A5B4FC]" /> Home · 100%</div>
      <div className="ml-1 my-2 h-6 border-l border-dashed border-white/20" />
      <div className="flex items-center gap-3 text-white text-xs"><BatteryCharging size={13} className="text-[#A5B4FC]" /> Ramanagara · charge 15 min</div>
      <div className="ml-1 my-2 h-6 border-l border-dashed border-white/20" />
      <div className="flex items-center gap-3 text-white text-xs"><MapPin size={13} className="text-[#4ADE80]" /> Mysuru · arrive 41%</div>
    </div>
    <div className="mt-4 rounded-xl bg-[#312E81]/40 border border-[#6366F1]/30 px-3 py-2.5 text-[#C7D2FE] text-[11px]">
      One reliable stop. No range anxiety.
    </div>
    <button className="mt-4 w-full rounded-full bg-white text-[#0A0A0C] text-xs font-medium py-2.5">Start navigation</button>
  </>
);

const ScreenHealth = () => (
  <>
    <StatusBar />
    <div className="text-white font-serif text-2xl leading-tight">Vehicle health</div>
    <div className="text-white/40 text-[11px] mt-1">Everything looks good</div>
    <div className="mt-4 flex items-center justify-center">
      <div className="relative h-28 w-28 rounded-full grid place-items-center" style={{ background: "conic-gradient(#6366F1 88%, rgba(255,255,255,0.08) 0)" }}>
        <div className="h-20 w-20 rounded-full bg-[#0A0A0C] grid place-items-center">
          <span className="text-white font-serif text-2xl">A+</span>
        </div>
      </div>
    </div>
    <div className="mt-4 space-y-2.5">
      {[[ShieldCheck, "Battery", "Healthy · 96%"], [Wrench, "Service", "Due in 1,200 km"], [Gauge, "Tyres", "Optimal"]].map(([Ic, n, s]) => (
        <div key={n} className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-3 py-2.5">
          <Ic size={15} className="text-[#A5B4FC]" />
          <div className="flex-1">
            <div className="text-white text-xs">{n}</div>
            <div className="text-white/40 text-[10px] font-mono">{s}</div>
          </div>
        </div>
      ))}
    </div>
  </>
);

const SCREENS = [
  { c: <ScreenRide />, label: "Ride · Home" },
  { c: <ScreenGrid />, label: "Ather Grid · Charging" },
  { c: <ScreenTrip />, label: "Trip planner" },
  { c: <ScreenHealth />, label: "Vehicle health" },
];

export default function AtherCaseStudy() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayO = useTransform(scrollYProgress, [0, 1], [0.5, 0.85]);

  useEffect(() => {
    document.title = "Ather Energy — Design Direction · Vatushi Moondra";
    return () => { document.title = "Vatushi Moondra — Senior UX Designer"; };
  }, []);

  return (
    <div className="relative bg-background">
      <HeaderNav overDark />

      {/* ---------- Kinetic dark hero ---------- */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#0A0A0C]">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img src="/projects/ather/hero-ride.jpg" alt="Rider on an Ather electric scooter in motion" className="h-full w-full object-cover" />
        </motion.div>
        <motion.div style={{ opacity: overlayO }} className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/40 to-[#0A0A0C]/60" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(60% 50% at 30% 40%, rgba(99,102,241,0.18), transparent 70%)" }} />

        <div className="relative z-10 h-full max-w-6xl mx-auto px-6 flex flex-col justify-end pb-20">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mb-6">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#A5B4FC]">
              Ather Energy · Design Direction
            </span>
          </motion.div>
          <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-8xl leading-[0.98] tracking-tight">
            <MaskLine delay={0.15}>Designing the</MaskLine>
            <MaskLine delay={0.28}>confidence to</MaskLine>
            <MaskLine delay={0.41}><span className="text-[#A5B4FC] italic">go electric.</span></MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 max-w-xl text-white/70 text-lg leading-relaxed"
          >
            A UX &amp; product-strategy engagement that turned range and charging anxiety into
            quiet, everyday confidence for the next generation of electric riders.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
          className="absolute bottom-6 right-6 z-10 flex items-center gap-2 text-white/40 font-mono text-[10px] uppercase tracking-widest"
        >
          <Zap size={12} className="text-[#A5B4FC]" /> Scroll
        </motion.div>
      </section>

      {/* ---------- Marquee ---------- */}
      <div className="border-y border-black/5 bg-secondary/40 py-5 font-mono text-sm uppercase tracking-[0.18em] text-foreground/70">
        <Marquee items={["UX Strategy", "User Research", "Design Direction", "Informed Design", "Design System", "Electric Mobility"]} />
      </div>

      {/* ---------- Meta ---------- */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            ["Role", "UX Strategy & Design Direction"],
            ["Timeline", "6 days engagement"],
            ["Sector", "Electric two-wheelers"],
            ["Deliverable", "Design Direction Document"],
          ].map(([k, v], i) => (
            <motion.div key={k} {...reveal} transition={{ ...reveal.transition, delay: i * 0.07 }}>
              <div className="overline text-primary/70">{k}</div>
              <div className="text-foreground mt-2 leading-snug">{v}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------- Chapters ---------- */}
      <Chapter n="—" label="Overview" title="Designing the direction, not just the screens.">
        <p>
          Ather Energy set out to win India's pragmatic, value-first riders — people who are excited
          by electric mobility but wary of its promises. Before designing a single screen, the
          product needed a shared, evidence-backed point of view on what to build and why.
        </p>
        <p>
          The Design Direction Document became that compass: aligning business vision, user reality
          and design intent across the vehicle dashboard, the companion app, and the charging
          ecosystem — the <span className="text-foreground font-medium">Ather Grid</span>.
        </p>
      </Chapter>

      <Chapter n="—" label="The Challenge" title="Range anxiety is a trust problem, not a battery problem.">
        <p>
          Riders don't abandon electric because of specs — they hesitate because of doubt. Will the
          range hold? Will a charger be free and working? Will a software glitch strand me?
        </p>
        <ul className="space-y-2.5 mt-2">
          {[
            "Optimistic range estimates that erode trust the moment they're wrong.",
            "Charging uncertainty and inconsistent public infrastructure.",
            "Information overload and alarming, high-cognitive-load interfaces.",
          ].map((p) => (
            <li key={p} className="flex gap-3"><span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" /><span>{p}</span></li>
          ))}
        </ul>
      </Chapter>

      {/* ---------- Product spotlight (parallax) ---------- */}
      <ProductSpotlight />

      {/* ---------- Personas ---------- */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-t border-black/5">
        <motion.div {...reveal} className="max-w-3xl mb-12">
          <div className="flex items-baseline gap-4">
            <span className="font-serif text-5xl text-primary/20 leading-none">03</span>
            <span className="overline text-primary">Research · Who we designed for</span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-foreground mt-5 leading-tight">
            Two riders, one shared need: to stop worrying.
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {PERSONAS.map((p, i) => (
            <motion.div
              key={p.name} data-testid="ather-persona"
              {...reveal} transition={{ ...reveal.transition, delay: i * 0.1 }}
              className="rounded-3xl border border-black/5 bg-white p-7 card-shadow"
            >
              <div className="flex items-center gap-4">
                <img src={p.photo} alt={p.name} className="h-16 w-16 rounded-2xl object-cover border border-black/5" />
                <div>
                  <h3 className="font-serif text-2xl text-foreground leading-tight">{p.name}</h3>
                  <div className="text-xs text-foreground/50 mt-1 font-mono uppercase tracking-widest">{p.meta}</div>
                </div>
              </div>
              <div className="mt-5 flex gap-3 text-foreground/70 italic">
                <Quote size={16} className="text-primary shrink-0 mt-1" />
                <p className="leading-relaxed">{p.quote}</p>
              </div>
              <div className="mt-6 grid gap-5">
                {[["Goals", p.goals], ["Pain points", p.pains], ["UX expectations", p.expects]].map(([label, list]) => (
                  <div key={label}>
                    <div className="overline text-primary/70 mb-2">{label}</div>
                    <ul className="space-y-1.5">
                      {list.map((it) => (
                        <li key={it} className="flex gap-2.5 text-sm text-foreground/70"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary/50" /><span>{it}</span></li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------- Informed Design chapters ---------- */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-t border-black/5">
        <motion.div {...reveal} className="max-w-3xl mb-14">
          <div className="flex items-baseline gap-4">
            <span className="font-serif text-5xl text-primary/20 leading-none">04</span>
            <span className="overline text-primary">Process</span>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl text-foreground mt-5 leading-tight">The practice of Informed Design</h2>
          <p className="mt-4 text-lg text-foreground/70 leading-relaxed">
            A scientific, staged methodology — each chapter moving from business truth toward a
            tested, buildable experience, and each producing a concrete deliverable.
          </p>
        </motion.div>

        <div className="divide-y divide-black/5 border-y border-black/5">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.n} data-testid="ather-phase"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55, delay: (i % 3) * 0.05 }}
              className="group grid md:grid-cols-12 gap-4 py-7 items-baseline hover:bg-secondary/40 transition-colors duration-300 px-2 -mx-2 rounded-lg"
            >
              <div className="md:col-span-1 font-serif text-3xl text-primary/30 group-hover:text-primary transition-colors">{c.n}</div>
              <h3 className="md:col-span-3 font-serif text-2xl text-foreground">{c.label}</h3>
              <p className="md:col-span-6 text-foreground/65 leading-relaxed">{c.body}</p>
              <div className="md:col-span-2 md:text-right">
                <span className="inline-block rounded-full bg-secondary text-primary font-mono text-[10px] uppercase tracking-widest px-3 py-1">{c.out}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------- Design tenets ---------- */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-t border-black/5">
        <motion.div {...reveal} className="max-w-3xl mb-10">
          <div className="flex items-baseline gap-4">
            <span className="font-serif text-5xl text-primary/20 leading-none">05</span>
            <span className="overline text-primary">Design principles</span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-foreground mt-5 leading-tight">
            An interface that earns trust, quietly.
          </h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TENETS.map((t, i) => {
            const Icon = t.icon;
            return (
              <motion.div key={t.title} {...reveal} transition={{ ...reveal.transition, delay: i * 0.06 }}
                className="rounded-2xl border border-black/5 bg-white p-5 card-shadow">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon size={18} /></span>
                <h3 className="font-serif text-lg text-foreground mt-4">{t.title}</h3>
                <p className="text-sm text-foreground/60 mt-2 leading-relaxed">{t.body}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ---------- Interface (app screens) ---------- */}
      <section className="border-t border-black/5 bg-[#0A0A0C] text-white py-20 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...reveal} className="max-w-3xl mb-12">
            <div className="flex items-baseline gap-4">
              <span className="font-serif text-5xl text-white/15 leading-none">06</span>
              <span className="overline text-[#A5B4FC]">Interface</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl mt-5 leading-tight">The companion app, screen by screen.</h2>
            <p className="mt-4 text-lg text-white/60 leading-relaxed">
              The full flow that turns the strategy into everyday confidence — honest range, visible
              charging on the Ather Grid, calm trip planning, and glanceable vehicle health.
            </p>
          </motion.div>
        </div>
        <div className="marquee-mask">
          <div className="flex gap-6 px-6 overflow-x-auto pb-4 max-w-6xl mx-auto" data-testid="ather-interface-gallery">
            {SCREENS.map((s, i) => (
              <motion.div
                key={i} data-testid="ather-screen"
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: (i % 4) * 0.08 }}
              >
                <PhoneFrame label={s.label}>{s.c}</PhoneFrame>
              </motion.div>
            ))}
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6">
          <p className="mt-8 text-xs text-white/35 font-mono uppercase tracking-widest">
            High-fidelity screens · design-direction concept
          </p>
        </div>
      </section>

      {/* ---------- Impact ---------- */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...reveal} className="rounded-3xl bg-[#0A0A0C] text-white p-10 md:p-16 relative overflow-hidden">
            <div className="absolute inset-0" style={{ background: "radial-gradient(60% 60% at 80% 20%, rgba(99,102,241,0.22), transparent 70%)" }} />
            <div className="relative z-10">
              <span className="overline text-[#A5B4FC]">07 · Impact</span>
              <h2 className="font-serif text-3xl md:text-5xl mt-4 max-w-2xl leading-tight">A compass the whole team could build against.</h2>
              <div className="mt-10 grid sm:grid-cols-3 gap-8">
                {[
                  ["1", "Unified direction across vehicle, app & charging grid"],
                  ["7", "Informed-Design phases, each with a shippable deliverable"],
                  ["100%", "Decisions traceable to a documented user insight"],
                ].map(([v, l]) => (
                  <div key={l}>
                    <div className="font-serif text-5xl text-[#A5B4FC]">{v}</div>
                    <div className="mt-2 text-white/60 text-sm leading-relaxed">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------- Learnings ---------- */}
      <Chapter n="08" label="Learnings">
        <p>
          Strategy is a design deliverable. The most valuable interface is often the alignment you
          create before anyone opens the design tool — and in electric mobility, honesty designs
          more confidence than horsepower ever could.
        </p>
      </Chapter>

      {/* ---------- Confidentiality ---------- */}
      <div className="max-w-5xl mx-auto px-6 pb-4">
        <div className="rounded-2xl bg-secondary px-5 py-4 text-sm text-foreground/70 flex items-start gap-3">
          <Bolt />
          <span>Selected interface details are abstracted and recreated for portfolio presentation, under the terms of a design-direction engagement with Ather Energy.</span>
        </div>
      </div>

      {/* ---------- CTA ---------- */}
      <div className="max-w-5xl mx-auto px-6 mt-10 mb-6 flex flex-wrap items-center justify-between gap-6 border-t border-black/5 pt-10">
        <Link to="/" data-testid="ather-home-cta" className="inline-flex items-center gap-2 text-primary hover:underline font-medium">
          <Home size={16} /> Back to home
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/#projects" className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-5 py-2.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors">
            More projects <ArrowUpRight size={15} />
          </Link>
          <a href={RESUME_URL} target="_blank" rel="noopener noreferrer" download
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-[hsl(242,47%,28%)] transition-colors">
            <Download size={15} /> Download Resume
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
}

/* ---------- Product spotlight with parallax ---------- */
const ProductSpotlight = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section ref={ref} className="max-w-6xl mx-auto px-6 py-10">
      <div className="relative overflow-hidden rounded-3xl bg-[#0A0A0C] aspect-[21/9]">
        <motion.img
          style={{ y }} src="/projects/ather/hero-ride.jpg" alt="Ather scooter detail"
          className="absolute inset-0 h-[116%] w-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "radial-gradient(50% 80% at 20% 50%, transparent, rgba(10,10,12,0.7))" }} />
        <div className="absolute bottom-0 left-0 p-8 md:p-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A5B4FC]">Product truth</div>
          <p className="mt-3 font-serif text-white text-2xl md:text-3xl max-w-lg leading-snug">
            The ride is effortless — the experience around it had to be, too.
          </p>
        </div>
      </div>
    </section>
  );
};
